import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Separator } from '@/components/ui/Separator';
import { SalesModule } from './sales-marketing/SalesModule';
import { MarketingModule } from './sales-marketing/MarketingModule';
import {
  isSalesChannelFilter,
  isSalesDashRange,
  SALES_DASH_RANGE_OPTIONS,
  salesDashComparisonLabel,
  salesDashWindow,
  type SalesChannelFilter,
  type SalesDashRange,
} from '@/features/sales-marketing/range';

/**
 * Sales & Marketing dashboard — the daily operating view that sits above
 * the deeper Marketing Dashboard.
 *
 * Two stacked modules share one date window (URL-synced, like the rest of
 * Marketing) so a standup paste of `?range=today` is the whole picture:
 * Shopify + Amazon revenue on top, paid + organic + campaigns underneath.
 * Channel (Shopify / Amazon / both) and chart-day / SKU drill-downs are
 * sales-only and also live in the URL.
 */

export function SalesMarketingDashboardPage() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();

  const range: SalesDashRange = isSalesDashRange(params.get('range'))
    ? (params.get('range') as SalesDashRange)
    : '30d';
  const channel: SalesChannelFilter = isSalesChannelFilter(params.get('channel'))
    ? (params.get('channel') as SalesChannelFilter)
    : 'all';
  const selectedDay = params.get('day');
  const selectedSku = params.get('sku');

  const period = salesDashWindow(range);
  const comparisonLabel = salesDashComparisonLabel(range);

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value == null || value === '') next.delete(key);
      else next.set(key, value);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['sales-marketing'] });
    queryClient.invalidateQueries({ queryKey: ['marketing'] });
  }, [queryClient]);

  return (
    <div className="space-y-8">
      <PageHeader
        title="Sales & Marketing"
        actions={
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        }
      />

      <div className="flex flex-wrap gap-1">
        {SALES_DASH_RANGE_OPTIONS.map((option) => (
          <Button
            key={option.id}
            size="sm"
            variant={range === option.id ? 'default' : 'outline'}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('range', option.id);
              next.delete('day');
              next.delete('sku');
              setParams(next, { replace: true });
            }}
          >
            {option.shortLabel}
          </Button>
        ))}
      </div>

      <SalesModule
        range={range}
        channel={channel}
        selectedDay={selectedDay}
        selectedSkuKey={selectedSku}
        onChannelChange={(next) => updateParam('channel', next === 'all' ? null : next)}
        onDayChange={(day) => updateParam('day', day)}
        onSkuChange={(sku) => updateParam('sku', sku)}
        comparisonLabel={comparisonLabel}
      />

      <Separator />

      <MarketingModule period={period} comparisonLabel={comparisonLabel} />
    </div>
  );
}
